import dayjs from 'dayjs';

import { ChevronRight, WarningRounded } from '@mui/icons-material';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import LinearProgress, {
  linearProgressClasses,
} from '@mui/material/LinearProgress';
import { styled } from '@mui/material/styles';
import { GridColDef, GridValueGetterParams } from '@mui/x-data-grid';

import { AdmissionKitDocuments } from '@octopus/api';
import { ContractTypes } from '@octopus/contract-types';

import { ExpandableTypography } from '../../../modules/components/ExpandableTypography';
import {
  companyColumn,
  departmentColumn,
} from '../../../modules/components/table/columns/CompanyColumns';
import {
  cargoColumn,
  workerCategoryColumn,
  workerNameAndPictureColumn,
} from '../../../modules/components/table/columns/WorkerColumns';
import { formatOptionalDayjsDate } from '../../../modules/formatters';
import { SnackbarType } from '../../../modules/hooks/snackbarContext';
import { TFFlags } from '../../fflags';

import {
  admissionDoneActionMenu,
  admissionDraftActionMenu,
  inProgressAdminActionMenu,
  inProgressCandidateActionMenu,
  inReviewActionMenu,
} from './actionMenus';
import { AdmissionTabs } from './types';

const admissionDateColumn: GridColDef = {
  field: 'admissionDate',
  flex: 1,
  renderHeader: () => <Box whiteSpace="nowrap">Data de admissão</Box>,
  valueGetter: (params: GridValueGetterParams) => {
    const { admissionDate } = params.row;

    return !admissionDate
      ? '-'
      : formatOptionalDayjsDate({ date: dayjs(admissionDate) });
  },
};

const limitDateColumn: GridColDef = {
  field: 'limitDate',
  flex: 1,
  renderHeader: () => <Box whiteSpace="nowrap">Data limite</Box>,
  valueGetter: (params: GridValueGetterParams) => {
    const { limitDate } = params.row;
    return !limitDate
      ? '-'
      : formatOptionalDayjsDate({ date: dayjs(limitDate) });
  },
};

const draftProgressColumn: GridColDef = {
  sortable: false,
  field: 'draftProgress',
  headerName: 'Etapa e progresso',
  valueGetter: (params: GridValueGetterParams) => {
    return {
      doneSteps: params.row.doneSteps,
      totalSteps: params.row.allSteps,
      draftStatus: params.row.draftStatus,
    };
  },
  renderCell: ({ value }) => {
    const { doneSteps, totalSteps, draftStatus } = value;
    if (doneSteps === 0 && draftStatus === 'invite_sent') {
      return (
        <ExpandableTypography color={'#B54708'}>
          Convite enviado
        </ExpandableTypography>
      );
    }
    const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
      height: 10,
      borderRadius: 5,
      [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor:
          theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
      },
      [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 5,
        backgroundColor:
          doneSteps === 0
            ? theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800]
            : theme.palette.mode === 'light'
              ? '#1a90ff'
              : '#308fe8',
      },
    }));
    return (
      <Tooltip
        placement="top"
        title={`Preenchidas ${doneSteps} de ${totalSteps} etapas`}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            border: '0px',
            padding: '0px',
          }}
        >
          <Box sx={{ width: '100%', mr: 1 }}>
            <BorderLinearProgress
              variant="determinate"
              value={(doneSteps * 100) / totalSteps}
              valueBuffer={100}
            />
          </Box>
          <Box sx={{ minWidth: 35 }}>
            <Typography variant="body2" color="text.secondary">
              {doneSteps}/{totalSteps}
            </Typography>
          </Box>
        </Box>
      </Tooltip>
    );
  },
};

const resumeColumn: GridColDef = {
  field: 'resumeDraft',
  headerName: '',
  valueGetter: (params: GridValueGetterParams) => {
    return {
      draftId: params.row.draftId,
    };
  },
  renderCell: ({ value }) => {
    const { draftId } = value;
    const redirectLink = `/admissions/new/${draftId}`;
    return createLinkColumn('Continuar', redirectLink);
  },
};
const reviewAdmissionColumn: GridColDef = {
  field: 'reviewAdmission',
  headerName: '',
  valueGetter: (params: GridValueGetterParams) => {
    return {
      draftId: params.row.draftId,
    };
  },
  renderCell: ({ value }) => {
    const { draftId } = value;
    const redirectLink = `/admissions/review/${draftId}`;
    return createLinkColumn('Revisar', redirectLink);
  },
};

const getAdmissionKitColumn = ({
  openGenerateKitDialog,
  downloadAdmissionKit,
  fflags,
}: {
  openGenerateKitDialog: ({
    open,
    contractId,
    draftId,
    admissionKitDocuments,
  }: {
    open: boolean;
    contractId: string;
    draftId: string;
    admissionKitDocuments: AdmissionKitDocuments;
  }) => void;
  downloadAdmissionKit: ({
    admissionKitDocuments,
    contractId,
  }: {
    admissionKitDocuments: AdmissionKitDocuments;
    contractId: string;
  }) => void;
  fflags?: TFFlags;
}): GridColDef => {
  if (!fflags || !fflags.kitAdmissionalEnabled.enabled) {
    return undefined;
  }

  return {
    field: 'admissionKit',
    headerName: 'Documentos',
    minWidth: 104,
    valueGetter: (params: GridValueGetterParams) => {
      return {
        admissionKitStatus: params.row.admissionKitStatus,
        contractId: params.row.contractId,
        draftId: params.row.draftId,
        admissionKitDocuments: params.row.admissionKitDocuments,
      };
    },
    renderCell: ({ value }) => {
      const { admissionKitStatus, contractId, draftId, admissionKitDocuments } =
        value;

      const hasKit = admissionKitStatus === 'done';
      const color = hasKit ? 'secondary' : 'primaryAlt';
      const text = hasKit ? 'Baixar' : 'Gerar';
      const disabled = !contractId;
      const tooltip = disabled
        ? 'Admissão em processo de conclusão, tente novamente mais tarde.'
        : hasKit
          ? 'Baixar documentos do kit admissional'
          : 'Gerar kit admissional';

      return createButtonColumn({
        text,
        disabled,
        color,
        variant: 'contained',
        tooltip,
        onclick: () => {
          hasKit
            ? downloadAdmissionKit({ admissionKitDocuments, contractId })
            : openGenerateKitDialog({
                open: true,
                contractId,
                draftId,
                admissionKitDocuments,
              });
        },
      });
    },
  };
};

const viewDetailsColumn: GridColDef = {
  field: 'viewAdmission',
  headerName: '',
  sortable: false,
  valueGetter: (params: GridValueGetterParams) => {
    return {
      contractId: params.row.contractId,
      draftId: params.row.draftId,
    };
  },
  renderCell: ({ value }) => {
    const { contractId, draftId } = value;
    if (!contractId) {
      const redirectLink = `/admissions/new/${draftId}`;
      const tooltip = 'Perfil em processo de criação';
      return createLinkColumn('Ver Admissão', redirectLink, false, tooltip);
    }
    const redirectLink = `/people/${contractId}`;
    return createLinkColumn('Ver perfil', redirectLink, false, '');
  },
};

export function getActionMenuCol(
  tab: AdmissionTabs,
  showSnackbar: (snackbar: SnackbarType) => void,
  dialogsState?: {
    setShowDeleteAdmissionDialog: (open: boolean) => void;
    setShowAdminTakeOverDialog: (open: boolean) => void;
    setShowResendInviteDialog: (open: boolean) => void;
    setShowGenerateKitDialog?: ({
      open,
      contractId,
      draftId,
      admissionKitDocuments,
    }: {
      open: boolean;
      contractId: string;
      draftId: string;
      admissionKitDocuments: AdmissionKitDocuments;
    }) => void;
    setSelectedAdmissionDraft: ({
      draftId,
      name,
      email,
      contractId,
      contractType,
    }: {
      draftId: string;
      name?: string;
      email?: string;
      contractId?: string;
      contractType?: ContractTypes;
    }) => void;
  },
  fflags?: TFFlags,
) {
  if (tab === AdmissionTabs.admission_done) {
    if (!fflags || !fflags.kitAdmissionalEnabled.enabled) {
      return undefined;
    }
  }

  const col: GridColDef = {
    field: 'contextMenu',
    headerName: '',
    disableColumnMenu: false,
    cellClassName: 'context-menu-cell',
    sortable: false,
    valueGetter: (params: GridValueGetterParams) => ({
      draftId: params.row.draftId,
      contractId: params.row.contractId,
      organizationId: params.row.organizationId,
      contractType: params.row.contractType,
      name: params.row.name,
      email: params.row.email,
      doneSteps: params.row.doneSteps,
      admissionKitStatus: params.row.admissionKitStatus,
      admissionKitDocuments: params.row.admissionKitDocuments,
    }),
  };

  switch (tab) {
    case AdmissionTabs.draft_created:
      col.renderCell = ({ value }) =>
        admissionDraftActionMenu(value.draftId, dialogsState);
      break;
    case AdmissionTabs.in_progress_candidate:
      col.renderCell = ({ value }) => {
        const { name, draftId, email, doneSteps } = value;
        return inProgressCandidateActionMenu(
          draftId,
          name,
          email,
          doneSteps,
          dialogsState,
        );
      };
      break;
    case AdmissionTabs.in_review:
      col.renderCell = ({ value }) =>
        inReviewActionMenu(
          {
            draftId: value.draftId,
          },
          dialogsState,
        );
      break;
    case AdmissionTabs.in_progress_admin:
      col.renderCell = ({ value }) =>
        inProgressAdminActionMenu(value.draftId, dialogsState);
      break;
    case AdmissionTabs.admission_done:
      col.renderCell = ({ value }) => {
        return admissionDoneActionMenu(
          value.draftId,
          value.contractId,
          value.admissionKitStatus,
          value.admissionKitDocuments,
          value.contractType,
          dialogsState,
        );
      };
      break;
    default:
      return undefined;
  }
  return col;
}

export const getColumnsByTab = ({
  tab,
  openGenerateKitDialog,
  downloadAdmissionKit,
  fflags,
}: {
  tab: AdmissionTabs;
  openGenerateKitDialog: ({
    open,
    contractId,
    draftId,
    admissionKitDocuments,
  }: {
    open: boolean;
    contractId: string;
    draftId: string;
    admissionKitDocuments: AdmissionKitDocuments;
  }) => void;
  downloadAdmissionKit: ({
    admissionKitDocuments,
    contractId,
  }: {
    admissionKitDocuments: AdmissionKitDocuments;
    contractId: string;
  }) => void;
  fflags?: TFFlags;
}): GridColDef[] => {
  const admissionKitColumn = getAdmissionKitColumn({
    openGenerateKitDialog,
    downloadAdmissionKit,
    fflags,
  });

  const columnsByTab: Record<AdmissionTabs, GridColDef[]> = {
    [AdmissionTabs.draft_created]: [
      workerNameAndPictureColumn(),
      cargoColumn(),
      departmentColumn(),
      companyColumn(),
      admissionDateColumn,
      limitDateColumn,
      workerCategoryColumn(),
      resumeColumn,
    ],
    [AdmissionTabs.in_progress_candidate]: [
      workerNameAndPictureColumn(),
      cargoColumn(),
      departmentColumn(),
      companyColumn(),
      admissionDateColumn,
      limitDateColumn,
      workerCategoryColumn(),
      draftProgressColumn,
    ],
    [AdmissionTabs.in_progress_admin]: [
      workerNameAndPictureColumn(),
      cargoColumn(),
      departmentColumn(),
      companyColumn(),
      admissionDateColumn,
      limitDateColumn,
      workerCategoryColumn(),
      draftProgressColumn,
    ],
    [AdmissionTabs.in_review]: [
      workerNameAndPictureColumn(),
      cargoColumn(),
      departmentColumn(),
      companyColumn(),
      admissionDateColumn,
      limitDateColumn,
      workerCategoryColumn(),
      reviewAdmissionColumn,
    ],
    [AdmissionTabs.admission_done]: [
      workerNameAndPictureColumn(),
      cargoColumn(),
      departmentColumn(),
      companyColumn(),
      admissionDateColumn,
      limitDateColumn,
      workerCategoryColumn(),
      ...(admissionKitColumn ? [admissionKitColumn] : []),
      viewDetailsColumn,
    ],
  };

  return columnsByTab[tab];
};

function createLinkColumn(
  text: string,
  redirectLink: string,
  disabled = false,
  tooltip = '',
) {
  return disabled ? (
    <Tooltip title={tooltip} placement="top">
      {disabled && <WarningRounded width="20px" color="disabled" />}
    </Tooltip>
  ) : (
    <Button
      sx={{ padding: '0px', borderRight: '0px', margin: '0px' }}
      variant="text"
      onClick={() => {
        window.open(redirectLink);
      }}
      disabled={disabled}
    >
      <Box sx={{ display: 'flex', whiteSpace: 'nowrap', width: '100%' }}>
        <Typography variant="body2" color="primary">
          {text}
        </Typography>
        <ChevronRight width="20px" />
      </Box>
    </Button>
  );
}

function createButtonColumn({
  text,
  disabled = false,
  tooltip = '',
  color = 'primary',
  variant = 'contained',
  onclick,
}: {
  text: string;
  disabled?: boolean;
  tooltip?: string;
  color?: 'primary' | 'secondary' | 'primaryAlt';
  variant?: 'contained' | 'outlined';
  onclick: () => void;
}) {
  return disabled ? (
    <Tooltip title={tooltip} placement="top" sx={{ width: '100%' }}>
      {disabled && <WarningRounded width="20px" color="disabled" />}
    </Tooltip>
  ) : (
    <Button
      sx={{ padding: '0px', margin: '0px', px: 1.5, py: 0.85, width: '100%' }}
      variant={variant}
      color={color}
      onClick={(evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        onclick();
      }}
      disabled={disabled}
    >
      <b style={{ fontWeight: 600, fontSize: 12 }}>{text}</b>
    </Button>
  );
}
